import { FilterChangeHandler, FilterToolbar } from '@/components/filter-toolbar';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { ScrollArea, ScrollBar } from '@/components/ui-kit/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { showErrorToast } from '@/hooks/use-toast';

import { EditSwaggerModal } from '@/modules/gpt-chats/components/tools/edit-swagger-modal';
import { getMethodColors } from '@/modules/gpt-chats/constants/tools';
import {
  useGetToolDetails,
  useToolAssociatedAgentCount,
} from '@/modules/gpt-chats/hooks/use-tools';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Bug, MoreVertical, Pencil, Trash } from 'lucide-react';
import Link from '@/components/core/router-link';
import { useRouter, useSearchParams } from '@/lib/react-router-compat';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useMemo, useState } from 'react';
import { DeleteActionModal } from '../components/tool-api-delete-action-modal/delete-action-modal';
import { useTranslation } from 'react-i18next';

interface ApiEntry {
  actionId: string;
  method: string;
  name: string;
  inputParameters: string[];
  testStatus: boolean;
  createTime: string;
}

const useApiFilterQueryParams = () => {
  const [queryParams, setQueryParams] = useQueryStates({
    pageNumber: parseAsInteger.withDefault(0),
    pageSize: parseAsInteger.withDefault(10),
    search: parseAsString.withDefault(''),
    testStatus: parseAsString.withDefault(''),
  });
  return { queryParams, setQueryParams };
};

export const ToolAPI = ({ toolId }: { toolId: string }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const { queryParams, setQueryParams } = useApiFilterQueryParams();
  const [isEditSwaggerOpen, setIsEditSwaggerOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const { data: toolDetails, refetch } = useGetToolDetails(toolId, isBlocksTool);
  const { data: associatedAgentCount } = useToolAssociatedAgentCount(toolId);

  const testStatusOptions = [
    { label: t('TESTED'), value: 'true' },
    { label: t('NOT_TESTED'), value: 'false' },
  ];

  const allApiData: ApiEntry[] = useMemo(() => {
    if (!toolDetails?.APIConfig?.Actions) return [];

    return Object.entries(toolDetails.APIConfig.Actions).map(([actionId, action]) => ({
      actionId: actionId,
      method: action.Method,
      name: action.Name,
      inputParameters: Object.keys(action.InputParameters || {}),
      testStatus: action.TestStatus,
      createTime: new Date(action.CreatedAt).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    }));
  }, [toolDetails]);

  const maxApiLimit = 10;
  const isMaxApiReached = allApiData.length >= maxApiLimit;

  const filteredData = useMemo(() => {
    let filtered = [...allApiData];

    if (queryParams.search) {
      const searchLower = queryParams.search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.method.toLowerCase().includes(searchLower) ||
          item.inputParameters.some((param) => param.toLowerCase().includes(searchLower))
      );
    }

    if (queryParams.testStatus) {
      const testStatusBool = queryParams.testStatus === 'true';
      filtered = filtered.filter((item) => item.testStatus === testStatusBool);
    }

    return filtered;
  }, [allApiData, queryParams.search, queryParams.testStatus]);

  const pageSize = 10;
  // const totalCount = filteredData.length;

  const paginatedData = useMemo(() => {
    const startIndex = queryParams.pageNumber * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, queryParams.pageNumber, pageSize]);

  const onChange: FilterChangeHandler<{ search: string; testStatus: string }> = (key, value) => {
    setQueryParams((prev) => ({
      ...prev,
      [key]: value,
      pageNumber: 0,
    }));
  };

  const onReset = () => {
    setQueryParams({ search: '', testStatus: '', pageNumber: 0, pageSize: 10 });
  };

  const handleSwaggerSave = () => {
    // Here you can add logic to save the updated schema
  };

  const columns = useMemo<ColumnDef<ApiEntry>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('PARAM_NAME'),
        cell: ({ row }) => {
          const method = row.original.method.toUpperCase();
          const colorClass = getMethodColors(method);

          return (
            <div className="flex max-w-[250px] items-center gap-2" title={row.getValue('name')}>
              <Badge
                variant="secondary"
                className={`pointer-events-none w-fit shrink-0 rounded-full ${colorClass}`}
              >
                {method}
              </Badge>
              <span className="truncate font-medium">{row.getValue('name')}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'inputParameters',
        header: t('TOOL_INPUT_PARAMETERS'),
        cell: ({ row }) => {
          const params = row.getValue('inputParameters') as string[];
          return (
            <div className="flex max-w-[400px] items-center gap-2 overflow-hidden">
              {params.map((param, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="pointer-events-none block w-fit max-w-[200px] shrink-0 truncate rounded-full bg-gray-100 text-left text-low-emphasis dark:bg-gray-800 dark:text-gray-300"
                  title={param}
                >
                  {param}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'testStatus',
        header: t('TEST_STATUS'),
        cell: ({ row }) => {
          const status = row.getValue('testStatus') as boolean;
          return (
            <Badge
              variant="secondary"
              className={`pointer-events-none w-fit rounded-full ${
                status
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {status ? t('TESTED') : t('NOT_TESTED')}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'createTime',
        header: t('CREATE_TIME'),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.getValue('createTime')}</span>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="cursor-pointer" asChild>
                <Link
                  href={`/create-api/${toolId}?actionId=${row.original.actionId}&isBlocksTool=${isBlocksTool}`}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('EDIT')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" asChild>
                <Link
                  href={`/create-api/${toolId}?actionId=${row.original.actionId}&step=4&isBlocksTool=${isBlocksTool}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedActionId(row.original.actionId);
                  }}
                >
                  <Bug className="mr-2 h-4 w-4" />
                  {t('DEBUGGING')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-error"
                onClick={(e) => {
                  e.stopPropagation();
                  if ((associatedAgentCount?.data as number) > 0) {
                    showErrorToast({ errors: t('TOOL_ASSOCIATED_WITH_AGENT') });
                    return;
                  }
                  setSelectedActionId(row.original.actionId);
                  setIsDeleteModalOpen(true);
                }}
              >
                <Trash className="mr-2 h-4 w-4" />
                {t('DELETE')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [toolId, associatedAgentCount, t, isBlocksTool]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Card className="rounded shadow-none">
        <CardContent className="p-0">
          <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
            <div className="w-full md:flex-1">
              <FilterToolbar
                filters={[
                  { key: 'search', type: 'SearchInput', label: t('SEARCH') },
                  {
                    key: 'testStatus',
                    type: 'Radio',
                    label: t('TEST_STATUS'),
                    props: {
                      options: testStatusOptions,
                    },
                  },
                ]}
                values={{
                  search: queryParams.search,
                  testStatus: queryParams.testStatus,
                }}
                defaultValues={{ search: '', testStatus: '' }}
                onChange={onChange}
                onReset={onReset}
              />
            </div>
            <div className="flex gap-3 md:justify-end">
              <Button
                variant="outline"
                onClick={() => setIsEditSwaggerOpen(true)}
                className="flex-1 md:w-auto md:flex-none"
              >
                {t('EDIT_SWAGGER')}
              </Button>
              <Link
                href={`/create-api/${toolId}?isBlocksTool=${isBlocksTool}`}
                className="flex-1 md:w-auto md:flex-none"
              >
                <Button className="w-full" disabled={isMaxApiReached}>
                  {t('CREATE_API')}
                </Button>
              </Link>
            </div>
          </div>
          {isMaxApiReached && (
            <div className="mx-4 mb-4 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <span>
                {t('MAX_API_LIMIT_REACHED', {
                  current: allApiData.length,
                  max: maxApiLimit,
                })}
              </span>
            </div>
          )}

          <ScrollArea className="w-full">
            <Table className="text-sm">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="hover:bg-transparent">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id} className="font-medium text-muted-foreground">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="font-normal"
                      // isHoverable
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={() =>
                        router.push(
                          `/create-api/${toolId}?actionId=${row.original.actionId}${isBlocksTool ? '&isBlocksTool=true' : ''}`
                        )
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {t('NO_RESULTS')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          {/* <div className="flex items-center justify-between border-t p-4">
            <div className="text-sm text-muted-foreground">
              Showing {queryParams.pageNumber * pageSize + 1}-
              {Math.min((queryParams.pageNumber + 1) * pageSize, totalCount)} of {totalCount}{" "}
              entries
            </div>
            <Pagination
              page={queryParams.pageNumber}
              pageSize={pageSize}
              totalCount={totalCount}
              pageSizeOptions={[10]}
              onChange={(newPage) => setQueryParams({ pageNumber: newPage })}
            />
          </div> */}
        </CardContent>
      </Card>

      <EditSwaggerModal
        isOpen={isEditSwaggerOpen}
        onClose={() => setIsEditSwaggerOpen(false)}
        onSave={handleSwaggerSave}
        toolId={toolId}
        isBlocksTool={isBlocksTool}
      />

      <DeleteActionModal
        toolId={toolId}
        actionId={selectedActionId}
        open={isDeleteModalOpen}
        onOpenChange={(open) => {
          setIsDeleteModalOpen(open);
          if (!open) {
            refetch();
          }
        }}
        isBlocksTool={isBlocksTool}
      />
    </>
  );
};
